import React, { useCallback, useEffect, useRef } from "react";
import {
  useLocation,
  useNavigate,
  useSearchParams,
  Link as RouterLink,
  Outlet,
} from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import moment from "moment";

import { SearchInput } from "../../components";
import Panels from "./Panels";
import AmazonEmbededView from "./AmazonEmbededView";
import { useSearch } from "../../hooks/useSearch";
import { resetSearchState } from "../../features/search/search.slice";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { selectExtension } from "../../features/extension/extension.selector";
import { selectCurrentPlan } from "../../features/pricingPlans/pricingPlans.selector";
import { selectUserPlanUsage } from "../../features/user/user.selector";
import { selectLookups } from "../../features/lookups/lookups.selector";
import { getLookupsThunk } from "../../features/lookups/lookups.thunk";
import { selectGeneralSettings } from "../../features/settings/settings.selector";
import { updateGeneralSettingsThunk } from "../../features/settings/settings.thunk";

function getTimeAgo(date: Date) {
  const now = moment();
  const lookupDate = moment(date);
  const diffMinutes = now.diff(lookupDate, "minutes");
  const diffHours = now.diff(lookupDate, "hours");
  const diffDays = now.diff(lookupDate, "days");

  if (diffMinutes < 60) {
    return `${diffMinutes}m ago`;
  }
  if (diffHours < 24) {
    return `${diffHours}h ago`;
  }
  if (diffDays === 1) {
    return "Yesterday";
  }
  if (diffDays < 7) {
    return `${diffDays}d ago`;
  }
  return lookupDate.format("MMM D");
}

function getLookupDisplay(lookup: {
  asin?: string;
  upc?: string;
  title?: string;
}) {
  if (lookup.asin) return lookup.asin;
  if (lookup.upc) return lookup.upc;
  if (lookup.title) return lookup.title;
  return "-";
}

const ProductAnalyzer = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const {
    asin: extensionAsin,
    isExtension,
    mode,
    amazonEmbededView,
  } = useAppSelector(selectExtension);
  const currentPlan = useAppSelector(selectCurrentPlan);
  const { analyzerLookupsUsed = 0 } = useAppSelector(selectUserPlanUsage);
  const { lookups } = useAppSelector(selectLookups);
  const { recentLookupsClearedAt } = useAppSelector(selectGeneralSettings);

  const lookupLimit = currentPlan?.features?.productAnalyzer?.lookups;
  const q = searchParams.get("q");
  const productsNotFound = searchParams.get("noProducts");
  const getSearchRoute = (q: string) => {
    return isExtension ? `/product-analyzer/search?q=${q}` : `/search?q=${q}`;
  };

  const { searchValue, setSearchValue, handleSearch, error, isLoading } =
    useSearch({
      onSuccess: ({ status, q }) => {
        if (status === "multiple_products") {
          navigate(getSearchRoute(q));
        } else if (status === "single_product") {
          setSearchParams({ asin: q });
        }
      },
    });

  const ref = useRef<HTMLInputElement>(null);

  const fetchRecentLookups = useCallback(() => {
    dispatch(
      getLookupsThunk({
        startDate: moment().subtract(7, "days").toDate(),
        endDate: new Date(),
        page: 1,
        perPage: 4,
        countryCode: "US",
      }),
    );
  }, [dispatch]);

  useEffect(() => {
    fetchRecentLookups();
  }, [fetchRecentLookups]);

  const currentAsin = searchParams.get("asin") || extensionAsin;
  const prevAsinRef = useRef<string | null>(null);
  useEffect(() => {
    if (currentAsin && currentAsin !== prevAsinRef.current) {
      prevAsinRef.current = currentAsin;
      const timer = setTimeout(() => {
        fetchRecentLookups();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [currentAsin, fetchRecentLookups]);

  useEffect(() => {
    if (q && productsNotFound) {
      setSearchValue(q);
    } else {
      dispatch(resetSearchState());
      setSearchValue("");
    }
  }, [dispatch, location, setSearchValue, q, productsNotFound]);

  useEffect(() => {
    if (error) {
      ref.current?.focus?.();
    }
  }, [error]);

  const showPanels =
    location.hash !== "#search" &&
    (searchParams.get("asin") || extensionAsin);

  if (showPanels) {
    if (
      isExtension &&
      mode === "embed_center" &&
      amazonEmbededView === "short"
    ) {
      return <AmazonEmbededView />;
    }
    return <Panels />;
  }

  const filteredLookups = recentLookupsClearedAt
    ? lookups.filter((l: any) => new Date(l.createdAt || l.date) > new Date(recentLookupsClearedAt))
    : lookups;

  return (
    <>
    <Stack spacing={2} sx={{ paddingY: "20px", paddingX: "12px", maxWidth: "500px" }}>
      <Stack>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: "18px",
            lineHeight: "27px",
            color: "#17191C",
          }}
        >
          Product Analyzer
        </Typography>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: "18px",
            lineHeight: "27px",
            color: "#17191C",
          }}
        >
          Advanced Sourcing Analysis
        </Typography>
        <Typography
          sx={{
            color: "#5A5F68",
            fontSize: "11px",
            lineHeight: "18px",
            fontWeight: 400,
          }}
        >
          For best results, use specific keywords, ASIN, UPC or ISBN code.
        </Typography>
      </Stack>

      <Stack spacing={2}>
        <Box
          sx={{
            position: "relative",
            width: "100%",
          }}
        >
          <SearchInput
            ref={ref}
            autoFocus
            value={searchValue}
            disabled={isLoading}
            onChange={(e) => setSearchValue(e.target.value)}
            placeholder="Search products..."
            onKeyDown={(e) => {
              if (e.code === "Enter") {
                handleSearch(searchValue);
              }
            }}
            sx={{
              width: "100%",
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
                border: "1px solid #DEDFE3",
                backgroundColor: "#FFFFFF",
                padding: "12px 24px",
                height: "48px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              },
              "& .MuiInputBase-input::placeholder": {
                color: "#5A5F68",
                fontSize: "16px",
                lineHeight: "26px",
                fontWeight: 400,
              },
            }}
          />
        </Box>

        <Button
          variant="contained"
          size="large"
          disabled={!searchValue.length || isLoading}
          onClick={() => handleSearch(searchValue)}
          endIcon={<ArrowForwardIcon />}
          sx={{
            backgroundColor: "#0F56CA",
            color: "#FFFFFF",
            fontWeight: 700,
            fontSize: "16px",
            lineHeight: "26px",
            padding: "12px 24px",
            borderRadius: "10px",
            height: "48px",
            textTransform: "uppercase",
            "&:hover": {
              backgroundColor: "#0E4FB8",
            },
            "&:disabled": {
              backgroundColor: "#E5E7EB",
              color: "#9CA3AF",
            },
          }}
        >
          Search
        </Button>

        {!!error && (
          <Typography variant="body2" color="error" sx={{ fontSize: "14px" }}>
            {error}
          </Typography>
        )}
      </Stack>

      {lookups && lookups.length > 0 && (
        <Stack spacing={2}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <HistoryIcon sx={{ fontSize: "20px", color: "#6B7280" }} />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  fontSize: "14px",
                  lineHeight: "21px",
                  color: "#17191C",
                }}
              >
                Recent Lookups
              </Typography>
            </Box>
            <Button
              startIcon={
                <DeleteOutlineIcon sx={{ fontSize: "16px !important" }} />
              }
              onClick={() => {
                dispatch(updateGeneralSettingsThunk({ recentLookupsClearedAt: new Date().toISOString() }));
              }}
              sx={{
                color: "#DC2626",
                fontSize: "12px",
                lineHeight: "20px",
                fontWeight: 500,
                "& .MuiButton-startIcon > *:nth-of-type(1)": {
                  fontSize: "16px !important",
                },
                textTransform: "none",
                minWidth: "auto",
                padding: "4px 8px",
              }}
            >
              Clear
            </Button>
          </Box>

          {filteredLookups.length > 0 && (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: filteredLookups.length === 1 ? "1fr" : "1fr 1fr",
              gap: "12px",
            }}
          >
            {filteredLookups
              .map((lookup: any, index: number) => (
                  <Card
                    key={lookup.lookupId || index}
                    sx={{
                      borderRadius: "12px",
                      border: "1px solid #C9C9C9",
                      boxShadow: "none",
                      backgroundColor: "#F4F4F6",
                      cursor: "pointer",
                      "&:hover": {
                        boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)",
                      },
                    }}
                    onClick={() => {
                      if (lookup.asin) {
                        setSearchParams({ asin: lookup.asin });
                      }
                    }}
                  >
                    <CardContent sx={{ padding: "12px !important" }}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "8px",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <AccessTimeIcon
                            sx={{ fontSize: "16px", color: "#6B7280" }}
                          />
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: "12px",
                              lineHeight: "18px",
                              fontWeight: 500,
                              color: "#17191C",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {getLookupDisplay(lookup)}
                          </Typography>
                        </Box>
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: "10px",
                            lineHeight: "15px",
                            fontWeight: 400,
                            color: "#5A5F68",
                            marginLeft: "24px",
                            display: "block",
                          }}
                        >
                          {getTimeAgo(
                            new Date(lookup.createdAt || lookup.date),
                          )}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
          </Box>
          )}
          <Button
            component={RouterLink}
            to="/history"
            sx={{
              color: "#0D54D8",
              fontSize: "12px",
              lineHeight: "20px",
              fontWeight: 500,
              textTransform: "none",
              alignSelf: "flex-start",
              padding: "4px 8px",
            }}
          >
            View Search History
          </Button>
        </Stack>
      )}
      <Divider
        sx={{
          height: "1px",
          backgroundColor: "#DEDFE380",
          opacity: 0.5,
          my: 0,
        }}
      />

      {lookupLimit && (
        <Box
          sx={{
            backgroundColor: "#EBF6FF80",
            borderRadius: "14px",
            padding: "16px",
            border: "1px solid #0F56CA4D",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box>
            <Typography
              variant="caption"
              sx={{
                fontSize: "11px",
                lineHeight: "17px",
                fontWeight: 700,
                color: "#5A5F68",
                textTransform: "uppercase",
                letterSpacing: "0.55px",
              }}
            >
              Your Plan Usage
            </Typography>
            <Typography
              sx={{
                fontSize: "18px",
                fontWeight: 700,
                color: "#1D4ED8",
                lineHeight: "27px",
                mt: 1,
              }}
            >
              {analyzerLookupsUsed}&nbsp;
              <span
                style={{
                  fontSize: "12px",
                  color: "#5A5F68",
                  lineHeight: "18px",
                  fontWeight: 400,
                }}
              >
                lookups used
              </span>
            </Typography>
          </Box>
          {lookupLimit && lookupLimit < 0 ? (
            <Box
              sx={{
                borderRadius: "13px",
                border: "1px solid #DEDFE3",
                color: "#17191C",
                backgroundColor: "#00000000",
                fontWeight: 700,
                fontSize: "12px",
                lineHeight: "20px",
                textTransform: "uppercase",
                padding: "6px 12px",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              Unlimited
            </Box>
          ) : lookupLimit && lookupLimit >= 0 ? (
            <Box
              sx={{
                borderRadius: "13px",
                border: "1px solid #DEDFE3",
                color: "#17191C",
                backgroundColor: "#00000000",
                fontWeight: 700,
                fontSize: "12px",
                lineHeight: "20px",
                textTransform: "uppercase",
                padding: "6px 12px",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              Limit: {lookupLimit}
            </Box>
          ) : null}
        </Box>
      )}
      <Typography
        variant="caption"
        sx={{
          fontSize: "10px",
          lineHeight: "15px",
          fontWeight: 400,
          color: "#5A5F68",
          display: "block",
          textAlign: "center",
        }}
      >
        Usage data refreshes every session. See your billing for limits.
      </Typography>
    </Stack>
    <Outlet />
    </>
  );
};

export default ProductAnalyzer;
